import * as React from "react";
import {
  Text,
  Button,
  Input,
  isNullish,
  DialogActions,
  DialogContent,
  DialogTitle,
  Modal,
  Icon,
  Flex,
  Select
} from "@theorgchartdev/storybook";
import CentralizedModalContainer from "../../../../Utils/CentralizedModalContainer/CentralizedModalContainer";
import { FieldSelectListProps } from "@/Utils/ComponentLib";
import { ComplexRuleInput } from "@/appComponents/Blocks/Composites/RuleInput/ComplexRuleInput/ComplexRuleInput";
import { useEffect, useState } from "react";
import { GetWSMgr } from "@/Utils/StateLib";
import { GetAllFieldNames } from "./Utils";
import { appstr } from "@coreapp/utils/appstr";
import { QFormulaRule } from "@coreapp/CoreApp/QFormulaRule";
import { kFormulaOptions, kFormulaScopeList } from "@coreapp/constants";
import { IsFormulaNameReserved } from "@coreapp/utils/IsFormulaNameReserved";

const SharedSelectProperties = {
  showCheck: true,
  optionSize: "small",
  bold: true,
  inputOnly: false,
  triggerProps: {
    modifier: "secondary",
    size: "small"
  }
};
export const DynamicFormulaEditorModal = ({
  chartConfig,
  name,
  onClose,
  creatingFormula,
  show,
  fieldIdx,
  handleUpdate,
  dataTestIdPrefix
}) => {
  const [editFormula, setEditFormula] = useState({});
  const [errors, setErrors] = useState({ name: "", rule: "" });
  const [showInput, setShowInput] = React.useState(true);
  const [showFields, setShowFields] = React.useState(false);
  const [showSpan, setShowSpan] = React.useState(false);
  const [showWarning, setShowWarning] = React.useState(false);
  const [editedRule, setEditRule] = useState(null);
  var curIndex;
  if (creatingFormula === false) {
    curIndex = fieldIdx;
  }

  useEffect(() => {
    InitializeFormula();
  }, []);

  const InitializeFormula = () => {
    let initFormula = {
      formulaName: "",
      formulaType: "math",
      formulaDef: ""
    };
    let formulas = chartConfig.scriptFormulas ?? [];
    if (creatingFormula === false) {
      initFormula = JSON.parse(JSON.stringify(formulas[fieldIdx]));
    }
    setEditFormula(initFormula);
    setEditRule(() => {
      const rule = new QFormulaRule();
      rule.InitFormulaRuleFromDB(initFormula);
      rule.SetComplexDef(initFormula.formulaDef);
      return rule;
    });
    BuildDynamicFormula(initFormula.formulaType);
  };

  const wsMgr = GetWSMgr();
  const curDoc = wsMgr?.GetDoc();

  const BuildFormulaTypes = () => {
    let options = [];
    for (var aFormulaType in kFormulaOptions) {
      let option = {};
      if (["lookup", "leftcalc", "rightcalc"].indexOf(kFormulaOptions[aFormulaType]) > -1) {
      } else {
        option.value = kFormulaOptions[aFormulaType];
        option.label = appstr(kFormulaOptions[aFormulaType]);
        option.key = "formulaType";
        option.clickHandler = UpdateFormula;
        options.push(option);
      }
    }
    return options;
  };
  const UpdateFormula = option => {
    setEditFormula({
      ...editFormula,
      [option.key]: option.value
    });
    if (option.key == "formulaType") BuildDynamicFormula(option.value);
  };

  const BuildDynamicFormula = option => {
    switch (option) {
      case "sumif":
        setShowSpan(true);
        setShowFields(true);
        setShowInput(true);
        setShowWarning(true);
        break;
      case "concatenate":
        setShowSpan(false);
        setShowFields(false);
        setShowInput(true);
        setShowWarning(false);
        break;
      case "averageif":
        setShowSpan(true);
        setShowFields(true);
        setShowInput(true);
        setShowWarning(true);
        break;
      case "countif":
        setShowSpan(true);
        setShowFields(false);
        setShowInput(true);
        setShowWarning(true);
        break;
      case "map":
        setShowSpan(false);
        setShowFields(true);
        setShowInput(true);
        setShowWarning(false);
        break;
      case "math":
        setShowSpan(false);
        setShowFields(false);
        setShowInput(true);
        setShowWarning(false);
        break;
      default:
        setShowSpan(true);
        setShowFields(true);
        setShowInput(false);
        setShowWarning(true);
        break;
    }
  };
  const BuildFormulaFieldsHeaders = () => {
    let headers = [];
    headers = chartConfig["headers"];
    let scriptFormulas = chartConfig["scriptFormulas"];
    if (scriptFormulas) {
      var scriptFieldNames = scriptFormulas.map(function (a) {
        return a["formulaName"];
      });
      headers = headers.concat(scriptFieldNames);
    }
    var internalFields = [
      "Span Of Control",
      "Subordinates",
      "Headcount",
      "Level",
      "SubChart Level",
      "Peers"
    ];
    return headers
      .concat(
        internalFields.filter(function (aField) {
          return headers.indexOf(aField) < 0;
        })
      )
      .sort();
  };
  const BuildFormulaFields = () => {
    let options = [];
    let headers = BuildFormulaFieldsHeaders();
    for (var aFieldIdx in headers) {
      let option = {};
      var theField = headers[aFieldIdx];
      if (theField !== chartConfig["formulaName"]) {
        option.value = headers[aFieldIdx];
        option.label = headers[aFieldIdx];
        option.key = "formulaField";
        option.clickHandler = UpdateFormula;
        options.push(option);
      }
    }
    return options;
  };
  const BuildFormulaSpans = [
    {
      label: "Self + Subordinates",
      value: kFormulaScopeList[0],
      key: "formulaScope",
      clickHandler: UpdateFormula
    },
    {
      label: "Subordinates",
      value: kFormulaScopeList[1],
      key: "formulaScope",
      clickHandler: UpdateFormula
    },
    {
      label: "Self + Direct Reports",
      value: kFormulaScopeList[2],
      key: "formulaScope",
      clickHandler: UpdateFormula
    },
    {
      label: "Direct Reports",
      value: kFormulaScopeList[3],
      key: "formulaScope",
      clickHandler: UpdateFormula
    }
  ];
  const GetName = () => {
    let name = editFormula.formulaName;
    return name ?? "";
  };
  const ValidateFormula = formulaDef => {
    const newErrors = { name: "", rule: "" };

    // Validate name
    if (formulaDef.formulaName.length < 1) {
      newErrors.name = appstr("formulaerrnoname");
    } else if (formulaDef.formulaName.includes(".")) {
      newErrors.name = appstr("Formula name cannot contain a period");
    } else if (IsFormulaNameReserved(formulaDef.formulaName, curDoc)) {
      newErrors.name = formulaDef.formulaName + appstr("formulareservedname");
    } else {
      const existingFieldNames = GetAllFieldNames(chartConfig, (key, idx) => {
        if (isNullish(curIndex) || key !== "scriptFormulas") {
          return true;
        }
        return idx !== parseInt(curIndex);
      });

      if (existingFieldNames.includes(formulaDef.formulaName)) {
        newErrors.name = appstr("formulaerrdupname");
      }
    }

    // Validate rule definition (only for formula types that use it)
    if (showInput && (!formulaDef.formulaDef || formulaDef.formulaDef.trim().length < 1)) {
      newErrors.rule = appstr("formulaerrnorule");
    } else if (formulaDef.formulaType === "map") {
      const formulaRule = new QFormulaRule();
      let validationResult = "";
      try {
        var v = formulaDef.formulaField;
        if (v == null) v = "";
        var vl = v.toString().toLowerCase();
        let s = formulaDef.formulaDef.toString();
        let doReplace = s.split('"').length == 1;
        if (doReplace) s = s.replaceAll("'", '"');
        s = "{" + s + "}";
        let obj = JSON.parse(s);
        var value = formulaRule.CheckMapCache(obj, vl);
        if (value == null) validationResult = "success";
      } catch (err) {
        validationResult = "refinvalid";
      }
      if (validationResult !== "success") {
        newErrors.rule = appstr(validationResult);
      }
    } else if (showInput) {
      const rule = new QFormulaRule();
      rule.InitFormulaRuleFromDB(formulaDef);
      rule.SetComplexDef(formulaDef.formulaDef);
      const validationResult = rule.ValidateRule();
      if (validationResult !== "success") {
        newErrors.rule = appstr(validationResult);
      }
    }

    setErrors(newErrors);
    return !newErrors.name && !newErrors.rule;
  };

  const SetDynFormulaName = option => {
    setErrors(prev => ({ ...prev, name: "" }));
    setEditFormula({
      ...editFormula,
      formulaName: option
    });
  };
  const handleRuleChange = rule => {
    setErrors(prev => ({ ...prev, rule: "" }));
    setEditRule(rule);
  };
  const BuildFormulaDef = () => {
    let formulaDefinition = editedRule.GetComplexDef();
    return {
      ...editFormula,
      formulaName: (editFormula.formulaName ?? "").trim(),
      formulaDef: formulaDefinition
    };
  };
  const SaveChanges = () => {
    const newFormula = BuildFormulaDef();
    let isValid = ValidateFormula(newFormula);
    if (!isValid) return false;
    let existingFormulas = chartConfig["scriptFormulas"];
    if (creatingFormula === false) {
      existingFormulas[curIndex] = newFormula;
      handleUpdate(existingFormulas, "scriptFormulas");
    } else {
      existingFormulas.push(newFormula);
      handleUpdate(existingFormulas, "scriptFormulas");
    }
    onClose();
  };

  return (
    <CentralizedModalContainer show={show}>
      <Modal
        id="create-dynamic-formula-modal"
        show={show}
        centerScreen={true}
        minWidth={"large"}
        maxWidth={"large"}
        customStyle={{ minHeight: "400px", maxHeight: "500px" }}
        className={"oc-modal-content"}
      >
        <DialogTitle divider={true} onClose={() => onClose()}>
          <span className={"ocn-explorer-icon-label"}>
            <Icon size={"medium"} name={"function"} />
            {creatingFormula ? appstr("Create dynamic formula") : appstr("Edit dynamic formula")}
          </span>
        </DialogTitle>
        <DialogContent customContentClass={"oc-modal-content-container"}>
          <Flex className={"oc-modal-content"} direction={"column"} gap={"8px"} height={"100%"}>
            <Input
              data-testid={"dynamic-formula-modal-input-name"}
              type="text"
              size="medium"
              value={GetName()}
              onChange={e => SetDynFormulaName(e.target.value)}
              label={appstr("Formula name")}
              message={errors.name}
              placeholder={appstr("Enter formula name")}
              status={errors.name ? "error" : null}
              require={true}
            />
            <Flex direction={"row"}>
              <Flex justifyContent={"space-between"} alignItems={"center"} width={"100%"}>
                <Flex className={"oc-left-justify"} padding={"4px"}>
                  <Select
                    dataTestId="dynamic-formula-modal-select-type"
                    label={appstr("Function")}
                    placeholder={editFormula.formulaType ?? appstr("Select formula type")}
                    selectSize={"medium"}
                    optionSize={"medium"}
                    listWidth={"fill"}
                    options={BuildFormulaTypes()}
                    value={editFormula.formulaType}
                    {...SharedSelectProperties}
                  />
                </Flex>
              </Flex>
              {showFields && (
                <Flex justifyContent={"space-between"} gap={"4px"} width={"100%"}>
                  <Flex alignItems={"center"} padding={"4px"}>
                    <Select
                      dataTestId={`${dataTestIdPrefix}-dynamic-formula-select-field`}
                      label={appstr("Field")}
                      placeholder={editFormula.formulaField ?? appstr("Select formula field")}
                      selectSize={"medium"}
                      optionSize={"medium"}
                      listWidth={"fill"}
                      options={BuildFormulaFields()}
                      listProps={FieldSelectListProps()}
                      value={editFormula.formulaField}
                      {...SharedSelectProperties}
                    />
                  </Flex>
                </Flex>
              )}
              {showSpan && (
                <Flex
                  justifyContent={"space-between"}
                  gap={"4px"}
                  alignItems={"center"}
                  width={"100%"}
                >
                  <Flex padding={"4px"} className={"oc-right-justify"}>
                    <Select
                      dataTestId={`${dataTestIdPrefix}-dynamic-formula-select-span`}
                      label={appstr("Span")}
                      placeholder={editFormula.formulaScope ?? appstr("Select formula span")}
                      selectSize={"medium"}
                      optionSize={"medium"}
                      listWidth={"fill"}
                      options={BuildFormulaSpans}
                      value={editFormula.formulaScope}
                      {...SharedSelectProperties}
                    />
                  </Flex>
                </Flex>
              )}
            </Flex>
            {showInput && (
              <ComplexRuleInput
                editing
                rule={editedRule}
                setRule={handleRuleChange}
                dataTestIdPrefix={"dynamic-formula"}
                require={true}
                errorMessage={errors.rule}
              />
            )}
          </Flex>
        </DialogContent>
        <DialogActions divider={true}>
          {showWarning && (
            <Flex className={"oc-left-justify"} padding={"4px"}>
              <Text colorModifier={"error"} size={"tiny"} type={"body"}>
                {appstr("DynFormWarn")}
              </Text>
            </Flex>
          )}
          <Flex className={"oc-right-justify"} padding={"4px"}>
            <DialogActions.Tertiary
              dataTestId={"dynamic-formula-modal-action-cancel"}
              size={"medium"}
              onClick={() => onClose()}
            >
              {appstr("Cancel")}
            </DialogActions.Tertiary>
            <DialogActions.Primary
              dataTestId={"dynamic-formula-modal-action-save"}
              size={"medium"}
              onClick={SaveChanges}
            >
              {appstr("Save")}
            </DialogActions.Primary>
          </Flex>
        </DialogActions>
      </Modal>
    </CentralizedModalContainer>
  );
};
